import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Box, Button, Grid, Typography } from "@mui/material";
import { get, sumBy, uniqueId } from "lodash";

import RefundApplicationItem from "./RefundApplicationItem";

const ChargesTypeItem = ({
  formik,
  t,
  index,
  isMaterialCharge = false,
  isDisabled,
  keyName,
  applications,
  applicationData: {
    applicationType,
    accessoriesList = [],
    totalMaterialCharges,
    totalLabourCharges,
    isDTA,
    billAmount,
    isIGSTExempted,
    supervisionCharge,
    totalCharges,
    sgst,
    cgst,
    igst,
  },
  headerName,
}) => {
  const { id: applicationTypeId, applicationId } = applicationType;
  const {
    setFieldValue,
    values: {
      consumerApplications,
      refundCalculation: {
        temporaryConnectionDeposit,
        depreciatedCostOfRecoveredMaterial,
        pendingDues,
        recoveryMaterialCost,
        disconnectionCharge,
        applicationMaterialCost,
      },
    },
    values,
  } = formik;

  const applicationCharges = useMemo(
    () =>
      applications?.reduce(
        (acc, { applicationId }) => {
          if (applicationId) {
            acc =
              get(
                values,
                `refundCalculation.applicationMaterialCost.${applicationId}`
              ) + acc;
          }
          return acc;
        },
        applicationMaterialCost[0] ? applicationMaterialCost[0] + 0 : 0
      ) ?? 0,
    [applications, applicationMaterialCost, values]
  );

  const handleAddAccessories = (
    keyName,
    existingAccessories,
    applicationType
  ) => {
    const addNewAccessories = {
      applicationType,
      id: uniqueId("temp_"),
      accessoryId: "",
      quantity: "",
      measurementTypeId: "",
      rate: "",
      totalMaterialCharges: 0,
      totalLabourCharges: 0,
      totalCharges: 0,
      supervisionCharge: 0,
      sgst: 0,
      cgst: 0,
      igst: 0,
      disconnectionCharge: 0,
      finalAmount: 0,
    };
    setFieldValue(keyName, [...existingAccessories, addNewAccessories]);
  };

  useEffect(() => {
    setFieldValue(
      `consumerApplications[${index}].${
        isMaterialCharge ? "totalMaterialCharges" : "totalLabourCharges"
      }`,
      +Math.round(sumBy(get(values, keyName), "finalCost")) ?? 0
    );
    const totalCharges =
      +consumerApplications[index].totalMaterialCharges +
      +consumerApplications[index].totalLabourCharges;
    setFieldValue(
      `consumerApplications[${index}].totalCharges`,
      applicationId
        ? Math.round(totalCharges)
        : Math.round(+consumerApplications[index].totalLabourCharges)
    );

    if (applicationId) {
      setFieldValue(
        `refundCalculation.applicationMaterialCost.${applicationId}`,
        Math.round(totalCharges)
      );
    }
  }, [accessoriesList, totalMaterialCharges, totalLabourCharges]);

  useEffect(() => {
    setFieldValue(
      `consumerApplications[${index}].supervisionCharge`,
      Math.round(+(0.15 * +consumerApplications[index].totalCharges))
    );
  }, [totalCharges]);

  useEffect(() => {
    if (isDTA) {
      setFieldValue(
        `consumerApplications[${index}].sgst`,
        Math.round(+(+consumerApplications[index].supervisionCharge * 0.09))
      );
      setFieldValue(
        `consumerApplications[${index}].cgst`,
        Math.round(+(+consumerApplications[index].supervisionCharge * 0.09))
      );
    } else if (!isIGSTExempted) {
      setFieldValue(
        `consumerApplications[${index}].igst`,
        Math.round(+(+consumerApplications[index].supervisionCharge * 0.18))
      );
    }
  }, [supervisionCharge, isDTA, isIGSTExempted]);

  useEffect(() => {
    const totalCost = Math.round(
      +(totalCharges + supervisionCharge + sgst + cgst + igst)
    );
    setFieldValue(`consumerApplications[${index}].finalAmount`, totalCost);
    if (!applicationId) {
      setFieldValue("refundCalculation.recoveryMaterialCost", totalCost);
    }
  }, [totalCharges, supervisionCharge, sgst, cgst, applicationTypeId, igst]);

  useEffect(() => {
    setFieldValue(
      "refundCalculation.finalAmount",
      +(
        temporaryConnectionDeposit -
        applicationCharges +
        depreciatedCostOfRecoveredMaterial -
        recoveryMaterialCost -
        disconnectionCharge -
        pendingDues +
        billAmount
      ).toFixed(2)
    );
  }, [
    temporaryConnectionDeposit,
    depreciatedCostOfRecoveredMaterial,
    recoveryMaterialCost,
    disconnectionCharge,
    applicationCharges,
    pendingDues,
    billAmount,
  ]);

  return (
    <Grid container columnSpacing={1} className="border border-2 m-1 mb-2 p-2">
      <Grid item xs={12}>
        <Typography className="mb-1" lineHeight="1" variant="h6">
          {t(headerName)}
        </Typography>
      </Grid>
      {!!accessoriesList.length &&
        accessoriesList.map((_, accessoriesIndex) => (
          <RefundApplicationItem
            applicationData={{
              accessoriesList,
            }}
            formik={formik}
            isDisabled={isDisabled}
            keyName={keyName}
            key={accessoriesIndex}
            accessoriesIndex={accessoriesIndex}
          />
        ))}
      <Box className="d-flex justify-content-between mb-1 w-100">
        <Button
          color="primary"
          size="small"
          sx={{
            minWidth: "unset",
            textTransform: "capitalize",
          }}
          disabled={isDisabled}
          variant="outlined"
          onClick={() =>
            handleAddAccessories(keyName, accessoriesList, applicationType)
          }
        >
          <strong>
            <span>+</span> {t("Add Accessories")}
          </strong>
        </Button>
        <Typography className="mb-1" lineHeight="1" variant="h6">
          {t("Total")} : {totalMaterialCharges ?? totalLabourCharges}
        </Typography>
      </Box>
    </Grid>
  );
};

ChargesTypeItem.propTypes = {
  applicationData: PropTypes.object,
  formik: PropTypes.object,
  t: PropTypes.func,
  applications: PropTypes.array,
  isMaterialCharge: PropTypes.bool,
  isDisabled: PropTypes.bool,
  index: PropTypes.num,
  keyName: PropTypes.string,
  headerName: PropTypes.string,
};

export default withTranslation()(ChargesTypeItem);
